import { Box, Paper, Stack } from '@mui/material';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useCallback, useEffect, useState } from 'react';
import type { OperationPanelState } from '~/state/atoms/toolsAtoms';
import PanelHeader from '~/components/toolbar/shared/PanelHeader';
import BooleanOperationContent from './boolean/BooleanOperationContent';
import MarginOperationContent from './margin/MarginOperationContent';
import RingOperationContent from './ring/RingOperationContent';

const PANEL_WIDTH = 320;

interface UnifiedOperationPanelProps {
    panelState: OperationPanelState;
    onClose: () => void;
    position: { x: number; y: number } | null;
}

function getPanelTitle(panelState: OperationPanelState): string {
    switch (panelState.type) {
        case 'boolean-operation':
            return 'Boolean Operation';
        case 'margin':
            return 'Margin';
        case 'ring':
            return 'Ring';
        default:
            return '';
    }
}

export default function UnifiedOperationPanel({
    panelState,
    onClose,
    position,
}: UnifiedOperationPanelProps) {
    const [closeDisabled, setCloseDisabled] = useState(false);

    const { attributes, listeners, setNodeRef, transform, isDragging } =
        useDraggable({ id: 'operations-panel' });

    const dragTransform = transform
        ? CSS.Translate.toString(transform)
        : undefined;

    // Escape key handler
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !closeDisabled) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [closeDisabled, onClose]);

    const handleClose = useCallback(() => {
        if (!closeDisabled) {
            onClose();
        }
    }, [closeDisabled, onClose]);

    if (!position) {
        return null;
    }

    const title = getPanelTitle(panelState);

    return (
        <Paper
            ref={setNodeRef}
            elevation={8}
            sx={{
                position: 'fixed',
                left: position.x,
                top: position.y,
                width: PANEL_WIDTH,
                zIndex: 1400,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                backgroundImage: 'none',
                transform: isDragging ? dragTransform : undefined,
                cursor: isDragging ? 'grabbing' : undefined,
            }}
            onClick={(e) => e.stopPropagation()}
            {...attributes}
        >
            <Box sx={{ p: 1.5 }}>
                <Stack spacing={1}>
                    <PanelHeader
                        title={title}
                        dragListeners={listeners}
                        onClose={handleClose}
                        closeDisabled={closeDisabled}
                    />

                    {panelState.type === 'boolean-operation' && (
                        <BooleanOperationContent onClose={onClose} />
                    )}

                    {panelState.type === 'margin' && (
                        <MarginOperationContent
                            mode={panelState.mode}
                            onClose={onClose}
                            onPreventCloseChange={setCloseDisabled}
                        />
                    )}

                    {panelState.type === 'ring' && (
                        <RingOperationContent
                            onClose={onClose}
                            onPreventCloseChange={setCloseDisabled}
                        />
                    )}
                </Stack>
            </Box>
        </Paper>
    );
}
